import React from 'react';
import { motion } from 'framer-motion';

const EmojiCard = ({ emoji, index, onClick, feedback, disabled, showCategory, category, isCorrectAnswer }) => {
  const isThisCard = feedback?.index === index;
  const isCorrect = isThisCard && feedback?.type === 'correct';
  const isWrong = isThisCard && feedback?.type === 'wrong';
  const showAsCorrect = (feedback?.type === 'wrong' || feedback?.type === 'timeout') && isCorrectAnswer;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onClick(index);
    }
  };

  // Determine card border style
  const getCardClass = () => {
    let classes = 'game-card w-full h-full min-h-0 flex flex-col items-center justify-center';
    if (isCorrect) classes += ' correct';
    if (isWrong) classes += ' wrong';
    if (showAsCorrect) classes += ' correct';
    return classes;
  };

  return (
    <motion.div className="flex flex-col items-center gap-1 h-full">
      <motion.button
        data-testid={`game-card-${index}`}
        type="button"
        className={getCardClass()}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ 
          opacity: 1, 
          scale: isCorrect ? [1, 1.1, 1] : showAsCorrect ? [1, 1.05, 1, 1.05, 1] : isWrong ? 1 : 1,
          x: isWrong ? [-10, 10, -10, 10, 0] : 0
        }}
        transition={{ 
          opacity: { duration: 0.2, delay: index * 0.05 },
          scale: { duration: showAsCorrect ? 1.5 : 0.3, repeat: showAsCorrect ? 2 : 0 },
          x: { duration: 0.4 }
        }}
        onClick={handleClick}
        disabled={disabled}
        style={{ 
          cursor: disabled ? 'default' : 'pointer',
          touchAction: 'manipulation',
          aspectRatio: '1'
        }}
      >
        {/* Emoji display - responsive size */}
        <span className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl select-none leading-none" role="img">
          {emoji}
        </span>
        
        {/* Correct answer overlay - only for the clicked correct card */}
        {isCorrect && (
          <motion.div 
            className="absolute inset-0 bg-[#06D6A0]/40 rounded-3xl flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-4xl sm:text-5xl text-white drop-shadow-lg"
            >
              ✓
            </motion.div>
          </motion.div>
        )}
        
        {/* Wrong answer overlay - only for the clicked wrong card */}
        {isWrong && (
          <motion.div 
            className="absolute inset-0 bg-[#EE6C4D]/40 rounded-3xl flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-4xl sm:text-5xl text-white drop-shadow-lg"
            >
              ✗
            </motion.div>
          </motion.div>
        )}

        {/* Highlight correct answer when wrong was selected - only for the correct card */}
        {showAsCorrect && (
          <motion.div 
            className="absolute inset-0 rounded-3xl flex items-center justify-center bg-[#06D6A0]/30"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              boxShadow: [
                '0 0 0 0 rgba(6, 214, 160, 0)',
                '0 0 0 6px rgba(6, 214, 160, 0.6)',
                '0 0 0 3px rgba(6, 214, 160, 0.3)',
                '0 0 0 6px rgba(6, 214, 160, 0.6)',
                '0 0 0 3px rgba(6, 214, 160, 0.3)',
                '0 0 0 6px rgba(6, 214, 160, 0.6)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl drop-shadow-lg"
            >
              👆
            </motion.div>
          </motion.div>
        )}
      </motion.button>

      {/* Category label below card - shown for all cards when feedback is given */}
      {showCategory && (
        <motion.span
          className={`text-xs font-quicksand font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
            isCorrectAnswer 
              ? 'bg-[#06D6A0]/20 text-[#06D6A0]' 
              : 'bg-slate-100 text-[#293241]/60'
          }`}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {category}
        </motion.span>
      )}
    </motion.div>
  );
};

export default EmojiCard;
